// @section 1 AuthBoot — minimal MSAL bootstrap for Atlas
// Atlas is internal-only; MSAL is always required. No magic-link/prospect paths.

import { type ReactNode, useEffect, useState } from "react";
import { EventType, type EventMessage, type AuthenticationResult } from "@azure/msal-browser";
import { msalInstance, resolveAuthReady } from "../lib/msalInstance";
import { loginRequest } from "../lib/msalConfig";
import { primeToken, getAccessToken } from "../lib/authFetch";

// @block 1.1 Strip stranded auth-response params from the query string.
// Azure can echo MSAL's response params (notably `state`) back on the QUERY
// string (`?state=<base64>`), but MSAL only sanitizes the hash. The leftover
// query param then persists across every navigation (BrowserRouter preserves
// it), clutters the address bar, and trips browser Safe-Browsing heuristics
// ("Dangerous"). Run this once handleRedirectPromise has consumed the hash.
function stripAuthQueryParams() {
  const url = new URL(window.location.href);
  const authKeys = [
    "state", "code", "session_state", "client_info",
    "error", "error_description", "error_uri",
    "id_token", "access_token", "token_type", "expires_in", "scope",
  ];
  let changed = false;
  for (const k of authKeys) {
    if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
  }
  if (!changed) return;
  const query = url.searchParams.toString();
  window.history.replaceState(null, "", url.pathname + (query ? `?${query}` : "") + url.hash);
}

export default function AuthBoot({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"loading" | "authed" | "anon">("loading");

  useEffect(() => {
    (async () => {
      try {
        await msalInstance.initialize();
        const result = await msalInstance.handleRedirectPromise();
        // MSAL has now consumed the hash; clear any auth params Azure stranded
        // on the query string so they don't persist in the address bar.
        stripAuthQueryParams();
        if (result) {
          msalInstance.setActiveAccount(result.account);
          // Use the idToken the redirect just issued instead of relying on
          // acquireTokenSilent (hidden-iframe renewal is what was failing,
          // wedging the app authed-but-tokenless behind the one-shot guard).
          if (result.idToken) primeToken(result.idToken, result.expiresOn);
        }
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          setState("anon");
          resolveAuthReady();
          return;
        }
        msalInstance.setActiveAccount(accounts[0]);

        // Resolve authReady BEFORE getAccessToken — the latter awaits the former.
        resolveAuthReady();

        // Block on token acquisition before declaring "authed". Without this,
        // children (MeProvider) mount and fire /api/hq/me before MSAL has
        // finished its silent-iframe renewal, and the request goes out with no
        // Authorization header → 401. By awaiting getAccessToken here we either
        // (a) hit the cache primed by handleRedirectPromise, (b) successfully
        // run acquireTokenSilent and prime the cache ourselves, or (c) fail and
        // trigger the one-shot interactive redirect (which navigates away
        // before this resolves — children never mount).
        //
        // (d) is the wedge: the one-shot redirect already fired this tab
        // (REAUTH_FLAG set) and STILL produced no token, so getAccessToken
        // returns null without navigating. Previously we declared "authed"
        // anyway and the app mounted tokenless, 401-storming every /api/hq/*
        // call with no recovery. Instead, fall back to the sign-in screen: its
        // "Sign in with Microsoft" button runs a fresh interactive loginRedirect
        // (immune to the third-party-cookie block that breaks silent renewal),
        // and the returning handleRedirectPromise primes the token + clears the
        // guard. An explicit sign-in beats a dead, tokenless app.
        const token = await getAccessToken();
        setState(token ? "authed" : "anon");
      } catch {
        setState("anon");
        resolveAuthReady();
      }
    })();

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const result = event.payload as AuthenticationResult;
        msalInstance.setActiveAccount(result.account);
        if (result.idToken) primeToken(result.idToken, result.expiresOn);
        setState("authed");
      }
    });
  }, []);

  if (state === "loading") {
    return <div className="auth-screen"><div>Loading…</div></div>;
  }
  if (state === "anon") {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>Atlas</h1>
          <p>Internal Vertis operating system.</p>
          <button
            className="auth-login"
            onClick={() => msalInstance.loginRedirect(loginRequest)}
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
